/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import {
  IBreakdownByMeterProps,
  IEmissionsByUtility,
  IEnergyUsageDetails,
  IMeter,
  IFuelType,
  IPortfolioUtilityCost,
  ISite,
  ITotalAggregation,
  IUtilityBillElect,
  IUtilityCost,
  AreaWiseAggregations,
  IPerformanceAggregationProps,
  ITotalAvoidanceAggregation
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { IAllMeterSavings } from '../../SavingsMeter/SavingsMeterProps';

/*
 *Returns array of values for totalActualEmission used in unit scaling
 */
export const getCarbonTotalEmissionValues = (
  scopeEmission: IEmissionsByUtility[] | IFuelType[]
) => {
  return scopeEmission?.map((scope) => {
    if (scope && scope.totalActualEmission) {
      return scope.totalActualEmission;
    } else {
      return 0;
    }
  });
};

export const getCarbonTotalAggregationValues = (
  scopeEmission: ITotalAggregation[]
) => {
  return scopeEmission?.map((scope) => {
    if (
      scope &&
      (scope.totalActualEmission || scope.totalPreviousPeriodEmission)
    ) {
      return Math.max(
        scope.totalActualEmission,
        scope.totalPreviousPeriodEmission
      );
    } else {
      return 0;
    }
  });
};

export const getAvoidanceTotalAggregationValues = (
  scopeEmission: ITotalAvoidanceAggregation[] | []
) => {
  return scopeEmission?.map((scope) => {
    if (
      scope &&
      (scope.totalActualAvoidance || scope.totalPreviousPeriodAvoidance)
    ) {
      return Math.max(
        scope.totalActualAvoidance,
        scope.totalPreviousPeriodAvoidance
      );
    } else {
      return 0;
    }
  });
};

export const getCarbonAvoidanceTotalAggregationValues = (
  scopeEmission: ITotalAvoidanceAggregation[]
) => {
  return scopeEmission?.map((scope) => {
    if (
      scope &&
      (scope.totalActualAvoidance || scope.totalPreviousPeriodAvoidance)
    ) {
      return Math.max(
        scope.totalActualAvoidance,
        scope.totalPreviousPeriodAvoidance
      );
    } else {
      return 0;
    }
  });
};

export const getCarbonUtilitySavingsValue = (
  scopeEmission: IFuelType[] | ISite[]
) => {
  return scopeEmission?.map((scope) => {
    if (
      scope &&
      (scope.totalPreviousPeriodEmission || scope.totalActualEmission)
    ) {
      return Math.max(
        scope.totalActualEmission,
        scope.totalPreviousPeriodEmission
      );
    } else {
      return 0;
    }
  });
};

/*
 *Returns array of values for totalConsumption used in unit scaling
 */
export const getEnergyTotalUsagesValues = (
  scopeEmission: IBreakdownByMeterProps[] | AreaWiseAggregations[]
) => {
  return scopeEmission?.map((scope) => {
    const convertedScopeActual = !scope.totalConsumption
      ? 0
      : scope.totalConsumption;
    const convertedScopeBaseline = !scope.totalBaselineConsumption
      ? 0
      : scope.totalBaselineConsumption;
    if (scope && (scope.totalConsumption || scope.totalBaselineConsumption)) {
      return Math.max(convertedScopeActual, convertedScopeBaseline);
    } else {
      return 0;
    }
  });
};

export const getUtilitySavingsMeter = (
  scopeEmission: IUtilityCost[] | IPortfolioUtilityCost[]
) => {
  return scopeEmission?.map((scope) => {
    if (
      scope &&
      (scope.aggregations.previousPeriodConsumption ||
        scope.aggregations.totalActualConsumption)
    ) {
      return Math.max(
        scope.aggregations.previousPeriodConsumption,
        scope.aggregations.totalActualConsumption
      );
    } else {
      return 0;
    }
  });
};
/*
 * getSavingsMeterForUtility Function type (IAllMeterSavings) added in or condition below
 */
export const getUtilityTotalAggregationValues = (
  scopeEmission: IUtilityBillElect[] | IAllMeterSavings[]
) => {
  return scopeEmission?.map((scope) => {
    if (
      scope &&
      (scope.previousPeriodConsumption || scope.totalActualConsumption)
    ) {
      return Math.max(
        scope.previousPeriodConsumption,
        scope.totalActualConsumption
      );
    } else {
      return 0;
    }
  });
};

/*
 *Returns array of values for totalConsumption used in unit scaling for Energy Graph
 */
export const getEnergyTotalUsagesGraphValues = (
  scopeEmission: IEnergyUsageDetails[] | IPerformanceAggregationProps[]
) => {
  return scopeEmission?.map((scope) => {
    if (scope && (scope.baselineConsumption || scope.consumption)) {
      return Math.max(scope.baselineConsumption!, scope.consumption!);
    } else {
      return 0;
    }
  });
};
